import React, { createContext, useMemo } from "react";
import { usePublicTenant } from "@/context/PublicTenantProvider";
import type {
  RestaurantConfigStatus,
  RestaurantConfiguration,
} from "@/domain/restaurant/restaurant.types";
import { useRestaurantConfigQuery } from "@/hooks/use-restaurant-config";
import { TenantBrowserChrome } from "@/hooks/use-tenant-browser-chrome";
import { TenantFavicon } from "@/hooks/use-tenant-favicon";
import { applyRestaurantLocaleConfig } from "@/utils/locale-format";

export type RestaurantConfigurationContextValue = {
  status: RestaurantConfigStatus;
  restaurantId: string;
  config: RestaurantConfiguration | null;
  restaurant_id: string | null;
  name: string | null;
  brand_name: string | null;
  currency_code: string | null;
  currency_symbol: string | null;
  country_iso2: string | null;
  timezone: string | null;
  require_allergen_consent: boolean | null;
  allergen_policy_pdf_url: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  theme_color: string | null;
  primary_button_color: string | null;
  secondary_button_color: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  contact_no: string | null;
  email: string | null;
  /** False until configuration has loaded (fails closed to Delivery-only). */
  website_pickup_enabled: boolean;
  error: Error | null;
  isLoading: boolean;
  isReady: boolean;
  refetch: (opts?: { force?: boolean }) => Promise<void>;
};

export const RestaurantConfigurationContext =
  createContext<RestaurantConfigurationContextValue | null>(null);

type RestaurantConfigurationProviderProps = {
  children: React.ReactNode;
};

function resolveStatus(
  restaurantId: string,
  isLoading: boolean,
  config: RestaurantConfiguration | undefined,
  error: Error | undefined
): RestaurantConfigStatus {
  if (!restaurantId.trim()) return "error";
  if (config) return "ready";
  if (error) return "error";
  if (isLoading) return "loading";
  return "idle";
}

export function RestaurantConfigurationProvider({
  children,
}: RestaurantConfigurationProviderProps) {
  const publicTenant = usePublicTenant();
  const restaurantId = publicTenant.restaurantId.trim();
  const slug = (publicTenant.slug ?? "").trim();
  const hostOnly = publicTenant.entryMode === "custom_hostname";
  const { data, error, isLoading, refetch } = useRestaurantConfigQuery({
    slug,
    restaurantId,
    hostOnly,
  });

  const status = resolveStatus(restaurantId, isLoading, data, error);
  const isReady = status === "ready" && data != null;

  if (data) {
    applyRestaurantLocaleConfig({
      currency_code: data.currency_code,
      currency_symbol: data.currency_symbol,
      country_iso2: data.country_iso2,
    });
  }

  const value = useMemo<RestaurantConfigurationContextValue>(
    () => ({
      status,
      restaurantId,
      config: data ?? null,
      restaurant_id: data?.restaurant_id ?? null,
      name: data?.name ?? null,
      brand_name: data?.brand_name ?? null,
      currency_code: data?.currency_code ?? null,
      currency_symbol: data?.currency_symbol ?? null,
      country_iso2: data?.country_iso2 ?? null,
      timezone: data?.timezone ?? null,
      require_allergen_consent: data?.require_allergen_consent ?? null,
      allergen_policy_pdf_url: data?.allergen_policy_pdf_url ?? null,
      logo_url: data?.logo_url ?? null,
      favicon_url: data?.favicon_url ?? null,
      theme_color: data?.theme_color ?? null,
      primary_button_color: data?.primary_button_color ?? null,
      secondary_button_color: data?.secondary_button_color ?? null,
      facebook_url: data?.facebook_url ?? null,
      instagram_url: data?.instagram_url ?? null,
      address: data?.address ?? null,
      city: data?.city ?? null,
      state: data?.state ?? null,
      zip_code: data?.zip_code ?? null,
      country: data?.country ?? null,
      latitude: data?.latitude ?? null,
      longitude: data?.longitude ?? null,
      contact_no: data?.contact_no ?? null,
      email: data?.email ?? null,
      website_pickup_enabled: data?.website_pickup_enabled === true,
      error: error ?? null,
      isLoading: status === "loading",
      isReady,
      refetch,
    }),
    [status, restaurantId, data, error, isReady, refetch]
  );

  return (
    <RestaurantConfigurationContext.Provider value={value}>
      <TenantFavicon />
      <TenantBrowserChrome />
      {children}
    </RestaurantConfigurationContext.Provider>
  );
}
